/**
 * Portal del inquilino.
 *
 * LA MISMA RUTA SIRVE A DOS PERSONAS DISTINTAS, y la pantalla lo sabe:
 *
 *   · La INQUILINA entra a SU casa. Se le saluda por su nombre, con la fecha de hoy, y todo
 *     está redactado en segunda persona. Es la única pantalla de su sesión: tiene que valer
 *     como aplicación completa, no como una pestaña más.
 *   · El GESTOR entra a comprobar qué ve ella. Conserva la cabecera de «vista previa», que es
 *     lo que esta pantalla es para él.
 *
 * El centro emocional es el PRÓXIMO PAGO: es lo que un inquilino viene a mirar. La tarjeta de
 * marca lo trata como titular —importe grande, cuenta atrás de días y una barra que se agota—
 * y cambia de registro cuando el recibo está vencido: ahí no hay cuenta atrás que valga.
 *
 * Pagar, reportar y escribir siguen siendo fase 3 (no hay pasarela, ni escritura del portal,
 * ni tiempo real). Los controles se quedan inertes con su explicación, como el resto de la
 * aplicación: prometer un botón que no hace nada es peor que decir que llegará.
 */
import { useQuery } from '@tanstack/react-query';
import type { CSSProperties } from 'react';
import type { PortalMessage } from '@naar/shared';
import { api, queryKeys } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { Badge, type BadgeTone } from '../components/Badge';
import { EmptyState, ErrorState } from '../components/EmptyState';
import { SkeletonCard, SkeletonPageHeader } from '../components/Skeleton';
import { formatEur } from '../lib/format';
import styles from './PortalPage.module.css';

const DAY_MS = 86_400_000;

/** Días entre hoy y una fecha `YYYY-MM-DD`, en días de calendario y sin zonas horarias. */
function daysUntil(isoDate: string): number {
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const [year, month, day] = isoDate.split('-').map(Number);
  return Math.round((Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1) - today) / DAY_MS);
}

function greetingFor(hour: number): string {
  if (hour < 7) return 'Buenas noches';
  if (hour < 14) return 'Buenos días';
  if (hour < 21) return 'Buenas tardes';
  return 'Buenas noches';
}

const TODAY_LABEL = new Intl.DateTimeFormat('es-ES', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

const TIME_LABEL = new Intl.DateTimeFormat('es-ES', { hour: '2-digit', minute: '2-digit' });

const DAY_LABEL = new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'long' });

const ISSUE_TONE: Record<string, BadgeTone> = {
  warning: 'warning',
  success: 'success',
  danger: 'danger',
  neutral: 'neutral',
};

export function PortalPage() {
  const { role } = useAuth();
  const isTenant = role === 'tenant';

  const { data, isPending, error, refetch } = useQuery({
    queryKey: queryKeys.portalMe,
    queryFn: api.portalMe,
  });

  if (isPending) {
    return (
      <section>
        <SkeletonPageHeader />
        <div className={styles.grid}>
          <div className={styles.left}>
            <SkeletonCard rows={1} height={150} />
            <SkeletonCard rows={2} height={40} />
          </div>
          <SkeletonCard rows={3} height={48} />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section>
        <PageHeader
          title="Portal del inquilino"
          subtitle="Pagos, incidencias y conversación con la gestión."
        />
        <ErrorState error={error} onRetry={() => void refetch()} />
      </section>
    );
  }

  const firstName = data.tenant.fullName.split(' ')[0] ?? data.tenant.fullName;
  const payment = data.nextPayment;
  const days = payment ? daysUntil(payment.dueDate) : null;
  const overdue = payment ? payment.status === 'impagado' || (days ?? 0) < 0 : false;
  const openIssues = data.issues.filter((issue) => issue.tone !== 'success').length;

  return (
    <section>
      {isTenant ? (
        /* La cabecera de la inquilina es un saludo, no un título de módulo: esta pantalla es
           toda su aplicación y debe recibirla como un sitio propio. */
        <header className={styles.hero} style={{ '--i': 0 } as CSSProperties}>
          <div>
            <p className={styles.heroDate}>{TODAY_LABEL.format(new Date())}</p>
            <h1 className={styles.heroTitle}>
              {greetingFor(new Date().getHours())}, {firstName}
            </h1>
          </div>
          <span className={styles.heroPlace}>
            <Icon name="pin" size={15} />
            {data.tenant.placement}
          </span>
        </header>
      ) : (
        <PageHeader
          title="Portal del inquilino"
          subtitle={`Vista previa de lo que ve ${data.tenant.fullName} · ${data.tenant.placement}`}
        />
      )}

      <div className={styles.grid}>
        <div className={styles.left}>
          {/* --- Próximo pago ------------------------------------------------ */}
          <Card
            variant="brand"
            className={overdue ? `${styles.payCard} ${styles.payCardOverdue}` : styles.payCard}
          >
            <div data-tour="portal-pago">
              <span className={styles.payDiamond} aria-hidden="true" />

              {payment ? (
                <>
                  <div className={styles.payLabel}>
                    {overdue ? 'Recibo vencido' : `Próximo pago · ${payment.dueLabel}`}
                  </div>
                  <div className={styles.payAmount}>{formatEur(payment.amount)}</div>

                  {overdue ? (
                    <p className={styles.payOverdueNote}>
                      Venció {days !== null && days < 0 ? `hace ${Math.abs(days)} día${Math.abs(days) === 1 ? '' : 's'}` : 'ya'}.
                      Si ya lo has pagado, la gestión lo verá reflejado; si no, escríbeles abajo.
                    </p>
                  ) : (
                    <div className={styles.payCountdown}>
                      {/* La barra se AGOTA hacia el vencimiento: leerla no requiere restar. */}
                      <div
                        className={styles.payTrack}
                        role="img"
                        aria-label={
                          days === 0
                            ? 'El recibo vence hoy'
                            : `Quedan ${days} día${days === 1 ? '' : 's'} para el vencimiento`
                        }
                      >
                        <span
                          className={styles.payFill}
                          style={{
                            width: `${Math.max(4, Math.min(100, ((days ?? 0) / 30) * 100))}%`,
                          }}
                        />
                      </div>
                      <span className={styles.payDays}>
                        {days === 0
                          ? 'vence hoy'
                          : days === 1
                            ? 'queda 1 día'
                            : `quedan ${days} días`}
                      </span>
                    </div>
                  )}

                  <div className={styles.payButton}>
                    <Button variant="onBrand" phaseTwo>
                      Pagar ahora
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.payLabel}>Próximo pago</div>
                  <div className={styles.payAmount}>Al día</div>
                  <p className={styles.payMeta}>
                    No tienes ningún recibo pendiente. Cuando se emita el del mes que viene,
                    aparecerá aquí con su fecha.
                  </p>
                </>
              )}
            </div>
          </Card>

          {/* --- Incidencias ------------------------------------------------- */}
          <Card variant="sm" className={styles.enter} style={{ '--i': 2 } as CSSProperties}>
            <div data-tour="portal-incidencias">
              <div className={styles.blockHead}>
                <div className={styles.blockTitle}>Mis incidencias</div>
                {openIssues > 0 && (
                  <span className={styles.blockCount}>
                    {openIssues} abierta{openIssues === 1 ? '' : 's'}
                  </span>
                )}
              </div>

              {data.issues.length === 0 ? (
                <EmptyState
                  inline
                  icon="wrench"
                  title="Nada roto, nada pendiente"
                  body="Cuando reportes una incidencia, aquí seguirás su estado sin tener que preguntar."
                />
              ) : (
                <ul className={styles.issues}>
                  {data.issues.map((issue) => (
                    <li key={issue.id} className={styles.issue}>
                      <span
                        className={`${styles.dot} ${styles[`dot_${issue.tone}`] ?? ''} ${
                          issue.tone !== 'success' ? styles.dotLive : ''
                        }`}
                        aria-hidden="true"
                      />
                      <span className={styles.issueTitle}>{issue.title}</span>
                      <Badge tone={ISSUE_TONE[issue.tone] ?? 'neutral'} size="sm">
                        {issue.statusLabel}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}

              <div className={styles.reportButton}>
                <Button variant="dashedBlock" phaseTwo>
                  + Reportar incidencia
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* --- Conversación --------------------------------------------------- */}
        <Card
          variant="sm"
          className={`${styles.chat} ${styles.enter}`}
          style={{ '--i': 3 } as CSSProperties}
        >
          <div className={styles.chatInner} data-tour="portal-chat">
            <div className={styles.blockHead}>
              <div className={styles.blockTitle}>
                {isTenant ? 'Conversación con la gestión' : 'Chat con el propietario'}
              </div>
              {data.conversation?.subject && (
                <span className={styles.chatSubject}>{data.conversation.subject}</span>
              )}
            </div>

            <div className={styles.messages}>
              {data.conversation && data.conversation.messages.length > 0 ? (
                <Conversation messages={data.conversation.messages} isTenant={isTenant} />
              ) : (
                <EmptyState
                  inline
                  icon="chat"
                  title="Sin mensajes todavía"
                  body="Escribe cuando necesites algo: la conversación queda guardada para los dos."
                />
              )}
            </div>

            <div className={styles.composer}>
              <div className={styles.composerField}>Escribe un mensaje…</div>
              <Button variant="primary" iconOnly phaseTwo aria-label="Enviar mensaje">
                <Icon name="send" size={18} strokeWidth={2} />
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
}

/**
 * Los mensajes se agrupan POR DÍA con un separador, y cada burbuja lleva su hora.
 *
 * Antes la hora vivía en un `title` que solo aparecía al dejar el ratón quieto encima — es
 * decir, nunca en un móvil—. En un chat sobre una fuga de agua, «cuándo se dijo» es la mitad
 * de la información.
 */
function Conversation({ messages, isTenant }: { messages: PortalMessage[]; isTenant: boolean }) {
  const groups: { day: string; items: PortalMessage[] }[] = [];

  for (const message of messages) {
    const day = DAY_LABEL.format(new Date(message.sentAt));
    const current = groups[groups.length - 1];
    if (current && current.day === day) current.items.push(message);
    else groups.push({ day, items: [message] });
  }

  return (
    <>
      {groups.map((group) => (
        <div key={group.day}>
          <div className={styles.daySeparator}>
            <span>{group.day}</span>
          </div>
          {group.items.map((message) => {
            const mine = isTenant ? message.from === 'tenant' : message.from === 'manager';
            return (
              <div
                key={message.id}
                className={mine ? `${styles.messageRow} ${styles.messageRowMine}` : styles.messageRow}
              >
                <div className={mine ? styles.bubbleMine : styles.bubbleTheirs}>
                  {message.body}
                </div>
                <span className={styles.messageMeta}>
                  {mine ? '' : `${message.authorName} · `}
                  {TIME_LABEL.format(new Date(message.sentAt))}
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
}
